/**
 * Player de vídeo ao vivo via MSE sobre WebSocket (protocolo go2rtc).
 * Só anuncia H.264: o MSE do Chrome trava com H.265 com o WS ainda recebendo
 * dados, o buffer do proxy enche e o backend fecha com 4002 slow-client.
 */

const VIDEO_CODECS = [
  'avc1.640029',
  'avc1.64002A',
  'avc1.640033',
  'avc1.4D4028',
  'avc1.4D4029',
  'avc1.42E01F'
];

const MAX_QUEUED_BYTES = 4 * 1024 * 1024;
const STALL_CHECK_MS = 1000;
const STUCK_TICKS_TO_RECONNECT = 4;
const KEEP_BEHIND_SECONDS = 8;
const LIVE_LAG_SECONDS = 5;

const CLOSE_REASONS = {
  4000: { state: 'closed', message: 'Sessão encerrada por tempo máximo de visualização.' },
  4001: { state: 'error', message: 'A câmera não está enviando vídeo (offline ou RTSP inacessível).' },
  4002: { state: 'stalled', message: 'O vídeo atrasou em relação ao vivo.' }
};

export const getMediaSourceClass = () => {
  if (typeof window === 'undefined') return null;
  return window.ManagedMediaSource || window.MediaSource || null;
};

export const isMseSupported = () => Boolean(getMediaSourceClass());

export const getSupportedCodecs = () => {
  const MediaSourceClass = getMediaSourceClass();
  if (!MediaSourceClass) return '';
  return VIDEO_CODECS
    .filter((codec) => MediaSourceClass.isTypeSupported(`video/mp4; codecs="${codec}"`))
    .join();
};

export class MseLivePlayer {
  /**
   * @param {HTMLVideoElement} video
   * @param {{ wsUrl: string, onState?: (state: string, detail?: string) => void, stallMs?: number }} options
   *   Estados: connecting | playing | stalled | closed | error
   */
  constructor(video, { wsUrl, onState, stallMs = 12000 }) {
    this.video = video;
    this.wsUrl = wsUrl;
    this.onState = onState || (() => {});
    this.stallMs = stallMs;

    this.ws = null;
    this.mediaSource = null;
    this.sourceBuffer = null;
    this.objectUrl = null;
    this.queue = [];
    this.queuedBytes = 0;
    this.lastDataAt = 0;
    this.lastCurrentTime = 0;
    this.stuckTicks = 0;
    this.state = 'idle';
    this.stallTimer = null;
    this.disposed = false;
  }

  setState(state, detail) {
    if (this.disposed || this.state === state) return;
    this.state = state;
    this.onState(state, detail);
  }

  start() {
    const MediaSourceClass = getMediaSourceClass();
    if (!MediaSourceClass) {
      this.setState('error', 'Este navegador não suporta reprodução de vídeo ao vivo (MSE).');
      return;
    }

    const codecs = getSupportedCodecs();
    if (!codecs) {
      this.setState('error', 'O navegador não suporta os codecs de vídeo desta câmera.');
      return;
    }

    this.setState('connecting');
    this.lastDataAt = Date.now();
    this.lastCurrentTime = 0;
    this.stuckTicks = 0;

    const mediaSource = new MediaSourceClass();
    this.mediaSource = mediaSource;
    mediaSource.addEventListener('sourceopen', () => this.openSocket(codecs), { once: true });

    if (window.ManagedMediaSource && mediaSource instanceof window.ManagedMediaSource) {
      this.video.disableRemotePlayback = true;
      this.video.srcObject = mediaSource;
    } else {
      this.objectUrl = URL.createObjectURL(mediaSource);
      this.video.src = this.objectUrl;
    }

    this.video.addEventListener('playing', this.handlePlaying);
    this.video.addEventListener('error', this.handleVideoError);
    this.stallTimer = setInterval(() => this.checkStall(), STALL_CHECK_MS);
  }

  handlePlaying = () => {
    this.setState('playing');
  };

  handleVideoError = () => {
    const error = this.video.error;
    this.setState('error', `Falha ao decodificar o vídeo${error && error.message ? `: ${error.message}` : ''}.`);
  };

  openSocket(codecs) {
    if (this.disposed) return;

    const ws = new WebSocket(this.wsUrl);
    ws.binaryType = 'arraybuffer';
    this.ws = ws;

    ws.addEventListener('open', () => {
      ws.send(JSON.stringify({ type: 'mse', value: codecs }));
    });
    ws.addEventListener('message', (event) => this.handleMessage(event));
    ws.addEventListener('close', (event) => this.handleClose(event));
    ws.addEventListener('error', () => {});
  }

  handleClose(event) {
    if (this.disposed) return;
    const known = CLOSE_REASONS[event.code];
    if (known) {
      this.setState(known.state, known.message);
      return;
    }
    this.setState('error', 'Não foi possível manter a conexão com o vídeo ao vivo.');
  }

  handleMessage(event) {
    if (typeof event.data === 'string') {
      let message;
      try {
        message = JSON.parse(event.data);
      } catch (error) {
        return;
      }
      if (message.type === 'mse') this.setupSourceBuffer(message.value);
      else if (message.type === 'error') {
        const raw = String(message.value || 'Erro no servidor de vídeo.');
        if (/codecs not matched/i.test(raw)) {
          this.setState(
            'error',
            'Esta câmera envia H.265 e o servidor ainda não converteu para H.264. Reabra o ao vivo depois de republicar o backend.'
          );
          return;
        }
        this.setState('error', raw);
      }
      return;
    }

    this.lastDataAt = Date.now();
    if (this.state === 'stalled') this.setState('connecting');
    this.queue.push(event.data);
    this.queuedBytes += event.data.byteLength;
    if (this.queuedBytes > MAX_QUEUED_BYTES) {
      this.queue = this.queue.slice(-8);
      this.queuedBytes = this.queue.reduce((sum, chunk) => sum + chunk.byteLength, 0);
    }
    this.flush();
  }

  setupSourceBuffer(mime) {
    const mediaSource = this.mediaSource;
    if (!mediaSource || mediaSource.readyState !== 'open' || this.sourceBuffer) return;

    let sourceBuffer;
    try {
      sourceBuffer = mediaSource.addSourceBuffer(mime);
    } catch (error) {
      this.setState('error', 'O navegador não consegue reproduzir o codec desta câmera.');
      return;
    }

    sourceBuffer.mode = 'segments';
    sourceBuffer.addEventListener('updateend', () => this.flush());
    this.sourceBuffer = sourceBuffer;
    this.ensurePlaying();
    this.flush();
  }

  ensurePlaying() {
    if (this.disposed || !this.video.paused) return;
    this.video.play().catch(() => {});
  }

  trimPlayed() {
    const sourceBuffer = this.sourceBuffer;
    if (!sourceBuffer || sourceBuffer.updating || !sourceBuffer.buffered.length) return false;
    const current = this.video.currentTime;
    if (current < KEEP_BEHIND_SECONDS) return false;
    const start = sourceBuffer.buffered.start(0);
    const trimTo = current - KEEP_BEHIND_SECONDS;
    if (trimTo - start < 3) return false;
    try {
      sourceBuffer.remove(start, trimTo);
      return true;
    } catch (error) {
      return false;
    }
  }

  catchUpLive() {
    const sourceBuffer = this.sourceBuffer;
    if (!sourceBuffer || !sourceBuffer.buffered || sourceBuffer.buffered.length === 0) return;
    const ranges = sourceBuffer.buffered;
    const end = ranges.end(ranges.length - 1);
    const current = this.video.currentTime;
    let inBuffer = false;
    for (let i = 0; i < ranges.length; i += 1) {
      if (current >= ranges.start(i) && current <= ranges.end(i)) {
        inBuffer = true;
        break;
      }
    }
    const livePosition = Math.max(ranges.start(ranges.length - 1), end - 0.25);
    if (!inBuffer || (current < 0.15 && end > 1) || end - current > LIVE_LAG_SECONDS) {
      this.video.currentTime = livePosition;
    }
  }

  flush() {
    const sourceBuffer = this.sourceBuffer;
    if (!sourceBuffer || sourceBuffer.updating) return;
    if (this.trimPlayed()) return;
    if (this.queue.length === 0) return;

    const next = this.queue.shift();
    this.queuedBytes = Math.max(0, this.queuedBytes - next.byteLength);
    try {
      sourceBuffer.appendBuffer(next);
    } catch (error) {
      if (error && error.name === 'QuotaExceededError') {
        this.queue.unshift(next);
        this.queuedBytes += next.byteLength;
        if (!this.trimPlayed()) {
          this.setState('stalled', 'Buffer do navegador cheio. Reconectando...');
        }
        return;
      }
      this.queue.unshift(next);
      this.queuedBytes += next.byteLength;
    }
  }

  checkStall() {
    if (this.disposed || this.state === 'closed' || this.state === 'error') return;

    this.ensurePlaying();
    this.catchUpLive();

    const receiving = Date.now() - this.lastDataAt < 4000;
    if (receiving && (this.state === 'playing' || this.state === 'connecting')) {
      const current = this.video.currentTime;
      if (Math.abs(current - this.lastCurrentTime) < 0.05) {
        this.stuckTicks += 1;
        if (this.stuckTicks >= STUCK_TICKS_TO_RECONNECT) {
          this.setState('stalled', 'O vídeo parou de avançar. Reconectando...');
        }
      } else {
        this.stuckTicks = 0;
        this.lastCurrentTime = current;
      }
    }

    if (Date.now() - this.lastDataAt > this.stallMs) {
      this.setState('stalled', 'Sem sinal da câmera.');
    }
  }

  stop() {
    this.disposed = true;
    clearInterval(this.stallTimer);
    this.stallTimer = null;

    this.video.removeEventListener('playing', this.handlePlaying);
    this.video.removeEventListener('error', this.handleVideoError);

    if (this.ws) {
      try { this.ws.close(); } catch (error) { /* já fechado */ }
      this.ws = null;
    }

    this.video.pause();
    this.video.removeAttribute('src');
    this.video.srcObject = null;
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.video.load();

    this.sourceBuffer = null;
    this.mediaSource = null;
    this.objectUrl = null;
    this.queue = [];
    this.queuedBytes = 0;
  }
}
